// Üzenet-RÉSZLETNÉZET — egyszerű read-only jobb oldali sheet (a FormSheet layout-mintája form nélkül):
// tárgy + EGY meta-sor (feladó avatar + nyelvhelyes név · küldés dátuma · „szerkesztve" · státusz-badge, ha
// nem elküldött) + SZANITÁLT HTML-törzs + link-sor + mellékletek.
// A törzs a detail-endpointról jön (a lista nem hordozza); a render CSAK a BFF-en allowlist-szanitált
// HTML-t kapja (contract: MessageDetailSchema.bodyHtml) → a dangerouslySetInnerHTML itt biztonságos
// (defense-in-depth a szerver-oldalon). Az automatikus olvasottnak-jelölést (messageMarkRead, optimistic)
// a LISTA végzi a sor-kattintáskor (a cache-kulcs gazdája) — ez a komponens csak megjelenít.
import { type MessageRowSchema } from '@atlas/contracts';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { MessageAttachments } from '@/components/messages/message-attachments';
import { MessageStatusBadge } from '@/components/messages/message-status-badge';
import { PersonChip } from '@/components/shared/person-chip';
import { Divider } from '@/components/ui/divider';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { messageKeys } from '@/features/messages/api';
import { api } from '@/lib/api';

type MessageRow = z.infer<typeof MessageRowSchema>;

export interface MessageDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A megnyitott üzenet lista-sora (fejléc-meta) — a törzs a detail-endpointról töltődik. */
  message: MessageRow | null;
  /** Melléklet feltöltés/törlés joga (messages.manage) — enélkül a melléklet-lista csak olvasható. */
  canManage: boolean;
}

/** ISO → lokalizált dátum+idő (a megnyitó nyelvén). */
function fmtDateTime(iso: string | null, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

export function MessageDetailSheet({
  open,
  onOpenChange,
  message,
  canManage,
}: MessageDetailSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const id = message?.id ?? '';

  const detailQ = api.messageDetail.useQuery(
    messageKeys.detail(id),
    { params: { id } },
    { queryKey: messageKeys.detail(id), enabled: open && id !== '' },
  );
  const detail = detailQ.data?.status === 200 ? detailQ.data.body : undefined;

  const links: { key: string; href: string }[] = detail
    ? [
        { key: 'googleDriveLink', href: detail.googleDriveLink ?? '' },
        { key: 'googleMeetLink', href: detail.googleMeetLink ?? '' },
        { key: 'zoomLink', href: detail.zoomLink ?? '' },
      ].filter((l) => l.href !== '')
    : [];

  const status = detail?.status ?? message?.status ?? '';
  const dateLine = fmtDateTime(
    detail?.sentAt ?? detail?.scheduledAt ?? detail?.createdAt ?? message?.sentAt ?? null,
    i18n.language,
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-background text-foreground gap-0 p-0">
        <div className="flex h-full min-h-0 flex-col">
          {/* FEJLÉC — tárgy + EGY meta-sor. A típus-badge (belső/külső) KIVEZETVE (user-döntés, 2026-08-25):
              a részletnézetben nem információ, csak a meta három sorba tördelte. Ami marad: a feladó avatarja +
              a NYELVHELYES sorrendű neve, a küldés dátuma, és — ha volt — a szerkesztés dátuma. A státusz-badge
              csak NEM-elküldött üzenetnél (piszkozat/időzített), mert az a sor ÁLLAPOTA. */}
          {/* A FEJLÉC alatt NINCS vonal (user-döntés, 2026-08-26): modál, felugró panel és beúszó lap fejléce
              alatt a hierarchiát a tipográfia és a térköz adja — ugyanaz a szabály, mint a `DialogHeader`-nél. */}
          <div className="shrink-0 px-5 py-4 pr-10">
            <SheetTitle>{message?.subject ?? detail?.subject ?? ''}</SheetTitle>
            <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              {message?.senderName && (
                // A feladó chipje a KÖZÖS `PersonChip` — a NÉV-RÉSZEKKEL, hogy a `formatName` a felület
                // nyelve szerint rendezze (HU: vezetéknév elöl). Korábban CSAK a `name` ment át, ezért a
                // részletnézet „Andrea Vivien Gorontay"-t írt magyar felületen is.
                <PersonChip
                  name={message.senderName}
                  givenName={message.senderGivenName}
                  familyName={message.senderFamilyName}
                  avatarUrl={message.senderAvatarUrl}
                  accentColor={message.senderTeamColor}
                  size="sm"
                  className="text-muted-foreground min-w-0 gap-1.5 text-sm"
                />
              )}
              {dateLine && (
                <Text variant="secondary" as="span">
                  {dateLine}
                </Text>
              )}
              {detail?.editedAt && (
                <Text variant="secondary" as="span">
                  {t('messages.detail.edited', {
                    date: fmtDateTime(detail.editedAt, i18n.language),
                  })}
                </Text>
              )}
              {status !== 'sent' && status !== '' && (
                <MessageStatusBadge
                  status={status}
                  label={t(`messages.statusLabel.${status}`, { defaultValue: status })}
                />
              )}
            </div>
          </div>

          {/* Görgethető törzs — a BFF-en szanitált HTML. */}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {detailQ.isLoading ? (
              <div className="space-y-3" aria-busy="true">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ) : !detail ? (
              <p className="text-muted-foreground text-sm">{t('messages.detail.loadError')}</p>
            ) : (
              <>
                <div
                  // A bodyHtml a BFF allowlist-szanitálásán átment HTML (contract-garancia) — nyers user-input
                  // SOSEM kerül ide. A tipográfia a tokenekből (nincs prose-plugin): bekezdés-köz + lista-stílus.
                  className="text-md text-foreground space-y-3 break-words [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_li]:ml-4 [&_ol]:list-decimal [&_ul]:list-disc"
                  dangerouslySetInnerHTML={{ __html: detail.bodyHtml }}
                />
                {links.length > 0 && (
                  <>
                    <Divider spacing="none" className="mt-5" />
                    <div className="space-y-1.5 pt-4">
                      {links.map((l) => (
                        <a
                          key={l.key}
                          href={l.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand flex items-center gap-1.5 text-sm hover:underline"
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0" />
                          <span className="truncate">{t(`messages.fields.${l.key}`)}</span>
                        </a>
                      ))}
                    </div>
                  </>
                )}
                {/* MELLÉKLETEK — a KÖZÖS panel (a jegy-csatolmány párja). A BEJÖVŐ (olvasás-)panelen:
                    • NINCS elválasztó vonal a linkek és a mellékletek között (user-döntés, 2026-08-25): a
                      link-sorok és a fájl-sorok ugyanabba az „ehhez az üzenethez tartozik" blokkba tartoznak,
                    • NINCS „Mellékletek N" cím (`hideHeader`): a fájl-sorok maguk hordozzák a nevet, a
                      feltöltőt és a dátumot — a cím + darabszám csak zaj volt.
                    A feltöltő-gomb (ha van joga a hívónak, azaz SAJÁT üzenetnél) a `hideHeader` mellett is
                    megjelenik — a cím-blokk marad el, nem a művelet. */}
                <div className="mt-4">
                  <MessageAttachments
                    messageId={id}
                    canManage={canManage}
                    enabled={open && id !== ''}
                    className="space-y-3"
                    hideHeader
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
